import { useCallback, useMemo, useState } from 'react';
import {
    useGetDashboardTodolists,
    useMoveDashboardTodolistStepMutation,
    useUpdateDashboardTodolistItemCompleteStatusMutation,
} from '#entities/dashboard';
import { type OpenApiSchemaDashboardTodolistItem } from '#shared/api/schema';
import { protect } from '#shared/lib/function';
import {
    readDashboardTodoListMainExpanded,
    writeDashboardTodoListMainExpanded,
} from '../lib/dashboardTodoListMainExpandedStorage';
import { type DashboardTodoListStepNavigationState } from '../lib/dashboardTodoListStepNavigation';
import { useDashboardTodoListLogging } from './useDashboardTodoListLogging';

export function useDashboardTodoList() {
    const [isMainExpanded, setIsMainExpanded] = useState(readDashboardTodoListMainExpanded);

    const { data: rawData, isFetching, isSuccess, isError } = useGetDashboardTodolists();
    const [updateItemCompleteStatus] = useUpdateDashboardTodolistItemCompleteStatusMutation();
    const [moveStep, { isLoading: isMovingStep }] = useMoveDashboardTodolistStepMutation();
    const [completingItemId, setCompletingItemId] = useState<string | null>(null);

    const onMainExpandToggle = useCallback((expanded: boolean) => {
        setIsMainExpanded(expanded);
        writeDashboardTodoListMainExpanded(expanded);
    }, []);

    const onToggleItemComplete = useCallback(
        (item: OpenApiSchemaDashboardTodolistItem) => {
            setCompletingItemId(item.id);

            void protect(async () => {
                await updateItemCompleteStatus({
                    id: item.id,
                    body: { is_complete: !item.completed_at },
                }).unwrap();
            }).finally(() => {
                setCompletingItemId(null);
            });
        },
        [updateItemCompleteStatus]
    );

    const currentTodolist = useMemo(() => {
        if (!isSuccess || rawData === undefined) {
            return undefined;
        }

        return rawData.todolists.find((todolist) => todolist.is_current) ?? null;
    }, [isSuccess, rawData]);

    const sortedItems = useMemo(() => {
        if (isError) {
            return null;
        }

        if (currentTodolist === undefined) {
            return undefined;
        }

        if (currentTodolist === null) {
            return null;
        }

        return [...currentTodolist.items].sort((a, b) => a.display_sequence - b.display_sequence);
    }, [currentTodolist, isError]);

    const title = currentTodolist?.title ?? '';

    const description = useMemo(() => {
        if (!sortedItems) {
            return '';
        }

        const hasItemsToComplete = sortedItems.filter((item) => !item.completed_at).length > 0;

        return hasItemsToComplete ? (currentTodolist?.description ?? '') : '';
    }, [sortedItems, currentTodolist?.description]);

    const stepNavigation = useMemo((): DashboardTodoListStepNavigationState | null => {
        if (!isSuccess || rawData === undefined) {
            return null;
        }

        const { can_move_forward, can_move_backward, move_backward_available_for_seconds } = rawData;

        if (!can_move_forward && !can_move_backward) {
            return null;
        }

        return {
            canMoveForward: can_move_forward,
            canMoveBackward: can_move_backward,
            moveBackwardAvailableForSeconds: move_backward_available_for_seconds,
        };
    }, [isSuccess, rawData]);

    const onMoveBackward = useCallback(() => {
        if (!currentTodolist) {
            return;
        }

        const currentType = currentTodolist.type;

        void protect(async () => {
            await moveStep({ direction: 'backward', current_type: currentType });
        });
    }, [moveStep, currentTodolist]);

    const onMoveForward = useCallback(() => {
        if (!currentTodolist) {
            return;
        }

        const currentType = currentTodolist.type;

        void protect(async () => {
            await moveStep({ direction: 'forward', current_type: currentType });
        });
    }, [moveStep, currentTodolist]);

    const isLoading = isFetching || (!isSuccess && !isError);

    const itemsKey = useMemo(() => (sortedItems ?? []).map((item) => item.id).join('|'), [sortedItems]);

    const shouldHideWidget = isError || (isSuccess && (currentTodolist === null || !sortedItems?.length));

    const isMovingBackward = Boolean(isMovingStep && stepNavigation?.canMoveBackward);
    const isMovingForward = Boolean(isMovingStep && stepNavigation?.canMoveForward);

    const {
        onToggleItemCompleteWithLogging,
        onMoveBackwardWithLogging,
        onMoveForwardWithLogging,
        onMarkdownLinkClick,
    } = useDashboardTodoListLogging({
        isMainExpanded,
        isLoading,
        sortedItems,
        currentTodolist,
        stepNavigation,
        onToggleItemComplete,
        onMoveBackward,
        onMoveForward,
    });

    return {
        isMainExpanded,
        onMainExpandToggle,
        completingItemId,
        onToggleItemComplete: onToggleItemCompleteWithLogging,
        sortedItems,
        isLoading,
        shouldHideWidget,
        itemsKey,
        title,
        description,
        stepNavigation,
        isMovingBackward,
        isMovingForward,
        onMoveBackward: onMoveBackwardWithLogging,
        onMoveForward: onMoveForwardWithLogging,
        onMarkdownLinkClick,
    };
}
